import React, { useState, useEffect } from 'react';
import {
  Activity,
  TrendingUp,
  AlertTriangle,
  BarChart3,
  Network,
  Zap,
  Clock,
  ArrowUpRight,
  ArrowDownRight,
  RefreshCw,
  Brain,
  Globe,
  Server,
  AlertCircle,
  CheckCircle,
  XCircle,
  ChevronDown,
  Loader2,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { apiClient, Cluster } from '../api/client';
import { cn } from '@/lib/utils';
import { PaginationControls } from '@/components/ui/pagination-controls';

// Interfaces for Traffic Insights data
interface TrafficStats {
  total_flows: number;
  total_l7_logs: number;
  total_traffic_volume: number; // bytes
  active_connections: number;
  suspicious_flows_count: number;
  time_range: string; // e.g., "Last 24 hours"
}

interface TopTalker {
  source: string; // namespace/workload or IP
  destination: string;
  protocol: string;
  port: number;
  flow_count: number;
  traffic_volume: number;
  is_suspicious: boolean;
}

interface ProtocolDistribution {
  protocol: string;
  count: number;
  percentage: number;
  is_suspicious: boolean;
}

interface SuspiciousTrafficPattern {
  source: string;
  destination: string;
  protocol: string;
  port: number;
  flow_count: number;
  reasons: string[];
}

interface ExternalTrafficSummary {
  ingress_flows: number;
  egress_flows: number;
  top_external_destinations: Array<{
    endpoint: string;
    flow_count: number;
    is_suspicious: boolean;
  }>;
  top_external_sources: Array<{
    endpoint: string;
    flow_count: number;
    is_suspicious: boolean;
  }>;
}

interface AIInsight {
  id: string;
  title: string;
  description: string;
  severity: 'critical' | 'high' | 'medium' | 'low' | 'info';
  category: string; // 'anomaly', 'security', 'optimization', 'compliance'
  recommendation: string;
  affected_count: number;
  confidence: number; // 0-100
}

interface TrafficInsightsData {
  stats: TrafficStats;
  top_talkers: TopTalker[];
  protocol_distribution: ProtocolDistribution[];
  suspicious_patterns: SuspiciousTrafficPattern[];
  external_traffic: ExternalTrafficSummary;
  ai_insights: AIInsight[];
  ai_analysis_timestamp?: string; // When AI analysis was last run
  traffic_timeline: Array<{
    timestamp: number;
    flow_count: number;
    suspicious_count: number;
    traffic_volume: number;
  }>;
}

export function TrafficInsights() {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [analyzingAI, setAnalyzingAI] = useState(false);
  const [selectedCluster, setSelectedCluster] = useState<string>('all');
  const [clusters, setClusters] = useState<Cluster[]>([]);
  const [insightsData, setInsightsData] = useState<TrafficInsightsData | null>(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [error, setError] = useState<string | null>(null);
  const [lastManualTrigger, setLastManualTrigger] = useState<number | null>(null);
  const [aiInsightsPage, setAiInsightsPage] = useState(0);
  const [topTalkersPage, setTopTalkersPage] = useState(0);
  const [externalSourcesPage, setExternalSourcesPage] = useState(0);
  const [externalDestsPage, setExternalDestsPage] = useState(0);
  const [aiSectionExpanded, setAiSectionExpanded] = useState(true); // Collapsible AI section
  const [cooldownAlert, setCooldownAlert] = useState<{ show: boolean; remainingSeconds: number }>({ show: false, remainingSeconds: 0 });
  const INSIGHTS_PER_PAGE = 3;
  const TOP_TALKERS_PER_PAGE = 10;
  const EXTERNAL_IPS_PER_PAGE = 5;

  useEffect(() => {
    loadInitialData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCluster]);

  const loadInitialData = async () => {
    setLoading(true);
    setError(null);
    try {
      await Promise.all([
        loadClusters(),
        loadTrafficInsights()
      ]);
    } catch (err) {
      console.error('Failed to load traffic insights:', err);
      setError('Failed to load traffic insights. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadClusters = async () => {
    try {
      const response = await apiClient.listClusters();
      setClusters(response.clusters || []);
    } catch (err) {
      console.error('Failed to load clusters:', err);
    }
  };

  const loadTrafficInsights = async () => {
    try {
      const response = await apiClient.getTrafficInsights(
        selectedCluster !== 'all' ? selectedCluster : undefined,
        false // don't force refresh - will use cache if available
      );
      setInsightsData(response);
      setAiInsightsPage(0); // Reset to first page when loading new data
      setTopTalkersPage(0);
      setExternalSourcesPage(0);
      setExternalDestsPage(0);
    } catch (err) {
      console.error('Failed to load traffic insights data:', err);
      throw err;
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await loadTrafficInsights();
    } finally {
      setRefreshing(false);
    }
  };

  const handleTriggerAIAnalysis = async () => {
    // Check 5-minute cooldown
    if (lastManualTrigger) {
      const timeSinceLastTrigger = Date.now() - lastManualTrigger;
      const fiveMinutes = 5 * 60 * 1000;
      if (timeSinceLastTrigger < fiveMinutes) {
        const remainingSeconds = Math.ceil((fiveMinutes - timeSinceLastTrigger) / 1000);
        setCooldownAlert({ show: true, remainingSeconds });
        // Auto-hide after 5 seconds
        setTimeout(() => setCooldownAlert({ show: false, remainingSeconds: 0 }), 5000);
        return;
      }
    }

    setAnalyzingAI(true);
    try {
      const response = await apiClient.getTrafficInsights(
        selectedCluster !== 'all' ? selectedCluster : undefined,
        true // force_refresh
      );
      setInsightsData(response);
      setAiInsightsPage(0); // Reset to first page with new insights
      setTopTalkersPage(0);
      setExternalSourcesPage(0);
      setExternalDestsPage(0);
      setLastManualTrigger(Date.now());
    } catch (err) {
      console.error('Failed to trigger AI analysis:', err);
      setError('Failed to generate AI insights. Please try again.');
    } finally {
      setAnalyzingAI(false);
    }
  };

  const formatTimestamp = (timestamp?: string): string => {
    if (!timestamp) return 'Never';
    try {
      const date = new Date(timestamp);
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffMins = Math.floor(diffMs / 60000);
      
      if (diffMins < 1) return 'Just now';
      if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
      
      const diffHours = Math.floor(diffMins / 60);
      if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
      
      return date.toLocaleString();
    } catch {
      return timestamp;
    }
  };

  const formatBytes = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const formatNumber = (num: number): string => {
    return new Intl.NumberFormat().format(num);
  };

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'critical':
      case 'high':
        return <AlertCircle className="h-4 w-4" />;
      case 'medium':
        return <AlertTriangle className="h-4 w-4" />;
      case 'low':
      case 'info':
        return <AlertCircle className="h-4 w-4" />;
      default:
        return <AlertCircle className="h-4 w-4" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-140px)]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error || !insightsData) {
    return (
      <div className="flex items-center justify-center h-full min-h-[400px]">
        <div className="text-center max-w-md">
          <XCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-lg font-semibold mb-2">Failed to Load Traffic Insights</h2>
          <p className="text-muted-foreground mb-4">{error || 'An unexpected error occurred.'}</p>
          <Button onClick={loadInitialData}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Retry
          </Button>
        </div>
      </div>
    );
  }

  const stats = insightsData.stats;

  return (
    <div className="space-y-6">
      {/* Cooldown Alert */}
      {cooldownAlert.show && (
        <Alert variant="destructive" className="border-orange-200 bg-orange-50 dark:bg-orange-950/20 relative">
          <Clock className="h-4 w-4" />
          <AlertTitle>Analysis Cooldown Active</AlertTitle>
          <AlertDescription>
            Please wait {cooldownAlert.remainingSeconds} seconds before triggering another analysis.
            The AI analysis is rate-limited to prevent excessive API usage.
          </AlertDescription>
          <button
            onClick={() => setCooldownAlert({ show: false, remainingSeconds: 0 })}
            className="absolute top-4 right-4 text-orange-600 hover:text-orange-800 dark:text-orange-400 dark:hover:text-orange-300"
            aria-label="Close alert"
          >
            <XCircle className="h-5 w-5" />
          </button>
        </Alert>
      )}

      {/* Filters Row */}
      <div className="flex flex-wrap items-center justify-end gap-3">
        <Select value={selectedCluster} onValueChange={setSelectedCluster}>
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="Select cluster" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Clusters</SelectItem>
            {clusters.map((cluster) => (
              <SelectItem key={cluster.id} value={cluster.id}>
                {cluster.cluster_name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          variant="outline"
          size="sm"
          onClick={handleRefresh}
          disabled={refreshing}
        >
          <RefreshCw className={cn("h-4 w-4 mr-2", refreshing && "animate-spin")} />
          Refresh
        </Button>
      </div>

      {/* Top-level Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Flows</p>
                <p className="text-2xl font-bold">{formatNumber(stats.total_flows + stats.total_l7_logs)}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {stats.total_flows > 0 && stats.total_l7_logs > 0 
                    ? `${formatNumber(stats.total_flows)} L3/L4 + ${formatNumber(stats.total_l7_logs)} L7`
                    : stats.total_l7_logs > 0
                    ? `${formatNumber(stats.total_l7_logs)} L7 logs`
                    : stats.time_range}
                </p>
              </div>
              <div className="h-12 w-12 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center">
                <Network className="h-6 w-6 text-blue-600 dark:text-blue-300" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Traffic Volume</p>
                <p className="text-2xl font-bold">{formatBytes(stats.total_traffic_volume)}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {stats.total_flows > 0 && stats.total_l7_logs > 0
                    ? 'L3/L4 + L7 combined'
                    : stats.total_l7_logs > 0
                    ? 'L7 access logs'
                    : stats.total_flows > 0
                    ? 'L3/L4 flows'
                    : 'No traffic data'}
                </p>
              </div>
              <div className="h-12 w-12 rounded-full bg-green-100 dark:bg-green-900 flex items-center justify-center">
                <BarChart3 className="h-6 w-6 text-green-600 dark:text-green-300" />
              </div>
            </div>
          </CardContent>
        </Card>

      </div>

      {/* AI Insights Banner - Highlight the differentiator */}
      <Card>
        <CardHeader className="cursor-pointer" onClick={() => setAiSectionExpanded(!aiSectionExpanded)}>
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <ChevronDown className={cn(
                "h-5 w-5 text-muted-foreground transition-transform flex-shrink-0",
                !aiSectionExpanded && "-rotate-90"
              )} />
              <div className="min-w-0">
                <CardTitle className="flex flex-wrap items-center gap-2">
                  <Brain className="h-5 w-5 flex-shrink-0" />
                  AI-Powered Traffic Analysis
                </CardTitle>
                <CardDescription className="mt-2">
                  Advanced insights from AI agents analyzing your network traffic patterns
                  {insightsData.ai_analysis_timestamp && (
                    <span className="block mt-1 text-xs">
                      <Clock className="h-3 w-3 inline mr-1" />
                      Last analyzed: {formatTimestamp(insightsData.ai_analysis_timestamp)}
                    </span>
                  )}
                </CardDescription>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                handleTriggerAIAnalysis();
              }}
              disabled={analyzingAI}
              className="gap-2"
            >
              <Brain className={cn("h-4 w-4", analyzingAI && "animate-pulse")} />
              {analyzingAI ? 'Analyzing...' : 'Trigger Analysis'}
            </Button>
          </div>
        </CardHeader>
        {aiSectionExpanded && (
          <CardContent>
            {!insightsData.ai_insights || insightsData.ai_insights.length === 0 ? (
            <div className="text-center py-8">
              <Brain className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground">
                No AI insights generated yet. Click "Trigger Analysis" to analyze current traffic patterns.
              </p>
            </div>
          ) : (
            <>
              <div className="space-y-3">
                {(insightsData.ai_insights || [])
                  .slice(aiInsightsPage * INSIGHTS_PER_PAGE, (aiInsightsPage + 1) * INSIGHTS_PER_PAGE)
                  .map((insight) => (
                    <div
                      key={insight.id}
                      className="p-4 rounded-lg border bg-card"
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          {getSeverityIcon(insight.severity)}
                          <h4 className="font-semibold text-sm">{insight.title}</h4>
                          <Badge variant="secondary" className="text-xs font-normal">
                            {insight.category}
                          </Badge>
                        </div>
                        <p className="text-sm text-muted-foreground mb-3">{insight.description}</p>
                        <div className="rounded px-3 py-2 text-sm bg-muted/50">
                          <p className="font-medium text-xs text-muted-foreground mb-1">Recommendation</p>
                          <p className="text-sm">{insight.recommendation}</p>
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
              
              {/* Pagination for AI Insights */}
              <PaginationControls
                page={aiInsightsPage}
                pageSize={INSIGHTS_PER_PAGE}
                totalItems={insightsData.ai_insights?.length ?? 0}
                onPageChange={setAiInsightsPage}
                itemLabel="insights"
              />
            </>
          )}
          </CardContent>
        )}
      </Card>

      {/* Main Content Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <div className="relative">
          <TabsList className="flex w-full overflow-x-auto md:grid md:grid-cols-4">
            <TabsTrigger value="overview" className="flex-shrink-0">Overview</TabsTrigger>
            <TabsTrigger value="top-talkers" className="flex-shrink-0">Top Talkers</TabsTrigger>
            <TabsTrigger value="protocols" className="flex-shrink-0">Protocols</TabsTrigger>
            <TabsTrigger value="external" className="flex-shrink-0">External</TabsTrigger>
          </TabsList>
          <div className="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-background to-transparent pointer-events-none md:hidden" />
        </div>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-1 gap-4">
            {/* Traffic Breakdown */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Activity className="h-5 w-5" />
                  Traffic Breakdown
                </CardTitle>
                <CardDescription>
                  Network and application layer traffic
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <p className="text-muted-foreground text-sm mb-1">Active Connections</p>
                    <p className="font-bold text-4xl">{formatNumber(stats.active_connections)}</p>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-muted rounded-lg p-3">
                      <p className="text-muted-foreground text-xs">L3/L4 Flows</p>
                      <p className="font-semibold text-2xl">{formatNumber(stats.total_flows)}</p>
                      <p className="text-xs text-muted-foreground mt-1">Network layer</p>
                    </div>
                    <div className="bg-muted rounded-lg p-3">
                      <p className="text-muted-foreground text-xs">L7 Requests</p>
                      <p className="font-semibold text-2xl">{formatNumber(stats.total_l7_logs)}</p>
                      <p className="text-xs text-muted-foreground mt-1">Application layer</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* External Traffic Summary */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Globe className="h-5 w-5" />
                External Traffic Overview
              </CardTitle>
              <CardDescription>
                Traffic flowing in and out of your clusters
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <ArrowDownRight className="h-4 w-4 text-green-500" />
                    <span className="text-sm font-medium">Ingress Traffic</span>
                  </div>
                  <p className="text-3xl font-bold">{formatNumber(insightsData.external_traffic.ingress_flows)}</p>
                  <p className="text-xs text-muted-foreground">Incoming flows from external sources</p>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <ArrowUpRight className="h-4 w-4 text-blue-500" />
                    <span className="text-sm font-medium">Egress Traffic</span>
                  </div>
                  <p className="text-3xl font-bold">{formatNumber(insightsData.external_traffic.egress_flows)}</p>
                  <p className="text-xs text-muted-foreground">Outgoing flows to external destinations</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Top Talkers Tab */}
        <TabsContent value="top-talkers" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5" />
                Top Talkers
              </CardTitle>
              <CardDescription>
                Workloads and services with the highest traffic volume
              </CardDescription>
            </CardHeader>
            <CardContent>
              {!insightsData.top_talkers || insightsData.top_talkers.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Network className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>No traffic data available for the selected time range</p>
                </div>
              ) : (
                <>
                  <div className="rounded-md border overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Source</TableHead>
                          <TableHead>Destination</TableHead>
                          <TableHead>Protocol</TableHead>
                          <TableHead className="text-right">Flows</TableHead>
                          <TableHead className="text-right">Volume</TableHead>
                          <TableHead>Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(insightsData.top_talkers || [])
                          .slice(topTalkersPage * TOP_TALKERS_PER_PAGE, (topTalkersPage + 1) * TOP_TALKERS_PER_PAGE)
                          .map((talker, idx) => (
                        <TableRow key={idx}>
                          <TableCell className="font-mono text-sm">{talker.source}</TableCell>
                          <TableCell className="font-mono text-sm">{talker.destination}</TableCell>
                          <TableCell>
                            <Badge variant="outline">{talker.protocol}</Badge>
                          </TableCell>
                          <TableCell className="text-right font-semibold">
                            {formatNumber(talker.flow_count)}
                          </TableCell>
                          <TableCell className="text-right">
                            {formatBytes(talker.traffic_volume)}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              {talker.is_suspicious ? (
                                <Badge variant="destructive" className="text-xs">
                                  <AlertTriangle className="h-3 w-3 mr-1" />
                                  Suspicious
                                </Badge>
                              ) : (
                                <Badge variant="outline" className="text-xs text-green-600">
                                  <CheckCircle className="h-3 w-3 mr-1" />
                                  Normal
                                </Badge>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                          ))}
                      </TableBody>
                    </Table>
                  </div>
                  
                  {/* Pagination for Top Talkers */}
                  <PaginationControls
                    page={topTalkersPage}
                    pageSize={TOP_TALKERS_PER_PAGE}
                    totalItems={insightsData.top_talkers?.length ?? 0}
                    onPageChange={setTopTalkersPage}
                    itemLabel="flows"
                    className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 mt-4"
                  />
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Protocols Tab */}
        <TabsContent value="protocols" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle>L3/L4 Protocol Distribution</CardTitle>
                <CardDescription>
                  Network layer traffic breakdown
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {(insightsData.protocol_distribution || [])
                    .filter(p => ['TCP', 'UDP', 'ICMP', 'SCTP'].includes(p.protocol))
                    .map((proto) => (
                      <div key={proto.protocol}>
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium">{proto.protocol}</span>
                            {proto.is_suspicious && (
                              <Badge variant="destructive" className="text-xs">
                                <AlertTriangle className="h-3 w-3 mr-1" />
                                Suspicious
                              </Badge>
                            )}
                          </div>
                          <span className="text-sm text-muted-foreground">
                            {formatNumber(proto.count)} ({proto.percentage.toFixed(1)}%)
                          </span>
                        </div>
                        <Progress value={proto.percentage} className="h-2" />
                      </div>
                    ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>L7 Protocol Distribution</CardTitle>
                <CardDescription>
                  Application layer traffic breakdown
                </CardDescription>
              </CardHeader>
              <CardContent>
                {(insightsData.protocol_distribution || [])
                  .filter(p => ['HTTP', 'HTTPS', 'gRPC', 'HTTP/2'].includes(p.protocol))
                  .length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Globe className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p>No L7 access logs detected</p>
                    <p className="text-xs mt-1">
                      L7 traffic requires Istio/Envoy service mesh
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {(insightsData.protocol_distribution || [])
                      .filter(p => ['HTTP', 'HTTPS', 'gRPC', 'HTTP/2'].includes(p.protocol))
                      .map((proto) => (
                        <div key={proto.protocol}>
                          <div className="flex items-center justify-between mb-1">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium">{proto.protocol}</span>
                              {proto.is_suspicious && (
                                <Badge variant="destructive" className="text-xs">
                                  <AlertTriangle className="h-3 w-3 mr-1" />
                                  Suspicious
                                </Badge>
                              )}
                            </div>
                            <span className="text-sm text-muted-foreground">
                              {formatNumber(proto.count)} ({proto.percentage.toFixed(1)}%)
                            </span>
                          </div>
                          <Progress value={proto.percentage} className="h-2" />
                        </div>
                      ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* External Traffic Tab */}
        <TabsContent value="external" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ArrowDownRight className="h-5 w-5 text-green-500" />
                  Top External Sources
                </CardTitle>
                <CardDescription>
                  External IPs connecting to your clusters
                </CardDescription>
              </CardHeader>
              <CardContent>
                {!insightsData.external_traffic?.top_external_sources || insightsData.external_traffic.top_external_sources.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Globe className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p>No external ingress traffic detected</p>
                  </div>
                ) : (
                  <>
                    <div className="space-y-2">
                      {(insightsData.external_traffic?.top_external_sources || [])
                        .slice(externalSourcesPage * EXTERNAL_IPS_PER_PAGE, (externalSourcesPage + 1) * EXTERNAL_IPS_PER_PAGE)
                        .map((source, idx) => (
                          <div key={idx} className="flex items-center justify-between gap-2 p-3 bg-muted rounded-lg">
                            <div className="flex items-center gap-2 min-w-0">
                              <Server className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                              <span className="font-mono text-sm truncate">{source.endpoint}</span>
                              {source.is_suspicious && (
                                <Badge variant="destructive" className="text-xs flex-shrink-0">
                                  Suspicious
                                </Badge>
                              )}
                            </div>
                            <span className="text-sm font-semibold whitespace-nowrap flex-shrink-0">{formatNumber(source.flow_count)} flows</span>
                          </div>
                        ))}
                    </div>
                    
                    {/* Pagination for External Sources */}
                    <PaginationControls
                      page={externalSourcesPage}
                      pageSize={EXTERNAL_IPS_PER_PAGE}
                      totalItems={insightsData.external_traffic?.top_external_sources?.length ?? 0}
                      onPageChange={setExternalSourcesPage}
                      itemLabel="sources"
                    />
                  </>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ArrowUpRight className="h-5 w-5 text-blue-500" />
                  Top External Destinations
                </CardTitle>
                <CardDescription>
                  External IPs your workloads are connecting to
                </CardDescription>
              </CardHeader>
              <CardContent>
                {!insightsData.external_traffic?.top_external_destinations || insightsData.external_traffic.top_external_destinations.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Globe className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p>No external egress traffic detected</p>
                  </div>
                ) : (
                  <>
                    <div className="space-y-2">
                      {(insightsData.external_traffic?.top_external_destinations || [])
                        .slice(externalDestsPage * EXTERNAL_IPS_PER_PAGE, (externalDestsPage + 1) * EXTERNAL_IPS_PER_PAGE)
                        .map((dest, idx) => (
                          <div key={idx} className="flex items-center justify-between gap-2 p-3 bg-muted rounded-lg">
                            <div className="flex items-center gap-2 min-w-0">
                              <Server className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                              <span className="font-mono text-sm truncate">{dest.endpoint}</span>
                              {dest.is_suspicious && (
                                <Badge variant="destructive" className="text-xs flex-shrink-0">
                                  Suspicious
                                </Badge>
                              )}
                            </div>
                            <span className="text-sm font-semibold whitespace-nowrap flex-shrink-0">{formatNumber(dest.flow_count)} flows</span>
                          </div>
                        ))}
                    </div>
                    
                    {/* Pagination for External Destinations */}
                    <PaginationControls
                      page={externalDestsPage}
                      pageSize={EXTERNAL_IPS_PER_PAGE}
                      totalItems={insightsData.external_traffic?.top_external_destinations?.length ?? 0}
                      onPageChange={setExternalDestsPage}
                      itemLabel="destinations"
                    />
                  </>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

    </div>
  );
}

